/**
 * APEXION WebSocket Hook
 * Real-time updates for dashboard, market data, and trades
 */

import { useEffect, useRef, useState, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Convert HTTP URL to WebSocket URL
const getWsUrl = (path) => {
  const url = new URL(BACKEND_URL);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  // WebSocket routes need /api prefix for proper routing through ingress
  const wsPath = path.startsWith('/api') ? path : `/api${path}`;
  return `${url.origin}${wsPath}`;
};

export const useWebSocket = (endpoint = '/ws/dashboard', options = {}) => {
  const {
    onMessage,
    onConnect,
    onDisconnect,
    onError,
    autoReconnect = false,
    reconnectInterval = 10000,
    maxReconnectAttempts = 1,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [isStableConnection, setIsStableConnection] = useState(false);  // New: only true after stable connection
  const [lastMessage, setLastMessage] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  
  const wsRef = useRef(null);
  const reconnectCountRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const stableTimeoutRef = useRef(null);

  const connect = useCallback(() => {
    // Get auth token
    const token = localStorage.getItem('apexion_token');
    const wsUrl = token 
      ? `${getWsUrl(endpoint)}?token=${token}`
      : getWsUrl(endpoint);

    setConnectionStatus('connecting');

    try {
      const ws = new WebSocket(wsUrl);
      // Quick timeout — if connection doesn't open in 3s, give up
      const connectTimeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          ws.close();
          setConnectionStatus('disconnected');
        }
      }, 3000);

      wsRef.current = ws;

      wsRef.current.onopen = () => {
        clearTimeout(connectTimeout);
        setIsConnected(true);
        setConnectionStatus('connected');
        reconnectCountRef.current = 0;
        onConnect?.();

        // Only show as stable after 2 seconds of successful connection
        stableTimeoutRef.current = setTimeout(() => {
          setIsStableConnection(true);
        }, 2000);

        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Skip pong messages
          if (data.type === 'pong') return;
          
          setLastMessage(data);
          onMessage?.(data);
        } catch (e) {
          console.error('WebSocket message parse error:', e);
        }
      };

      wsRef.current.onclose = () => {
        setIsConnected(false);
        setIsStableConnection(false);
        setConnectionStatus('disconnected');
        onDisconnect?.();

        // Clear timeouts and intervals
        if (stableTimeoutRef.current) {
          clearTimeout(stableTimeoutRef.current);
        }
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }

        // Auto reconnect
        if (autoReconnect && reconnectCountRef.current < maxReconnectAttempts) {
          setConnectionStatus('reconnecting');
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectCountRef.current += 1;
            connect();
          }, reconnectInterval);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionStatus('error');
        onError?.(error);
      };

    } catch (error) {
      console.error('WebSocket connection failed:', error);
      setConnectionStatus('error');
    }
  }, [endpoint, autoReconnect, reconnectInterval, maxReconnectAttempts, onMessage, onConnect, onDisconnect, onError]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (stableTimeoutRef.current) {
      clearTimeout(stableTimeoutRef.current);
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
    }
    setIsStableConnection(false);
  }, []);

  const send = useCallback((data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  const subscribe = useCallback((channel) => {
    send({ type: 'subscribe', channel });
  }, [send]);

  useEffect(() => {
    connect();
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    isConnected,
    isStableConnection,
    connectionStatus,
    lastMessage,
    send,
    subscribe,
    connect,
    disconnect,
  };
};

/**
 * Hook for market data WebSocket
 */
export const useMarketWebSocket = (onUpdate) => {
  const [marketData, setMarketData] = useState(null);

  const handleMessage = useCallback((message) => {
    if (message.type === 'market_update') {
      setMarketData(message.data);
      onUpdate?.(message.data);
    }
  }, [onUpdate]);

  const { isConnected, connectionStatus } = useWebSocket('/ws/market', {
    onMessage: handleMessage,
  });

  return {
    marketData,
    isConnected,
    connectionStatus,
  };
};

/**
 * Hook for dashboard WebSocket with all updates
 */
export const useDashboardWebSocket = (handlers = {}) => {
  const {
    onMarketUpdate,
    onTradeExecuted,
    onBotUpdate,
    onStatsUpdate,
    onAlert,
  } = handlers;

  const [data, setData] = useState({
    marketData: null,
    lastTrade: null,
    stats: null,
    bots: [],
    alerts: [],
  });

  const handleMessage = useCallback((message) => {
    switch (message.type) {
      case 'market_update':
        setData(prev => ({ ...prev, marketData: message.data }));
        onMarketUpdate?.(message.data);
        break;
      
      case 'trade_executed':
        setData(prev => ({ ...prev, lastTrade: message.data }));
        onTradeExecuted?.(message.data);
        break;
      
      case 'bot_update':
        setData(prev => ({
          ...prev,
          bots: prev.bots.map(bot => 
            bot.id === message.data.id ? { ...bot, ...message.data } : bot
          ),
        }));
        onBotUpdate?.(message.data);
        break;
      
      case 'dashboard_update':
        setData(prev => ({ ...prev, stats: message.data }));
        onStatsUpdate?.(message.data);
        break;
      
      case 'alert':
        setData(prev => ({
          ...prev,
          alerts: [...prev.alerts.slice(-9), message.data],
        }));
        onAlert?.(message.data);
        break;
      
      default:
        break;
    }
  }, [onMarketUpdate, onTradeExecuted, onBotUpdate, onStatsUpdate, onAlert]);

  const ws = useWebSocket('/ws/dashboard', {
    onMessage: handleMessage,
    onConnect: () => {
      // Subscribe to all channels
      ws.subscribe('market');
      ws.subscribe('trades');
      ws.subscribe('bots');
    },
  });

  return {
    ...data,
    isConnected: ws.isStableConnection,  // Use stable connection to prevent flickering
    connectionStatus: ws.connectionStatus,
  };
};

export default useWebSocket;
